/**
 * The technician Stop: one dialog for "I'm done" (Stop while running, End Work
 * while paused), the same on desktop, tablet and phone.
 *
 * For the LAST technician on the ticket, Stop is the deliberate declaration
 * that the BOH work is complete: the server completes the ticket in the same
 * act (operational completion — not pickup, payment or closeout). It asks
 * only what OptiBay cannot know:
 *   - another technician still running or paused → nothing about the vehicle
 *     or the ticket; only this technician's work ends;
 *   - the last technician, with jobs no measured work points at → what
 *     happened to each (completed / not performed), or return to work;
 *   - the last technician, vehicle not already parked on site → "Where are
 *     you leaving the vehicle?" (parking spot / general parking / exact spot
 *     unknown — a completed car stays on site);
 *   - the last technician, vehicle already parked on site → it stays there
 *     (unless the technician says it is somewhere else).
 * #19: when a fresh exact camera sighting answers the parking question, it is
 * pre-answered — "Found it in Spot 12" · [Change] — and the Stop submits the
 * SAME finish_my_work (TECH_PARK). A camera suggestion only highlights a spot.
 * No camera, or any doubt: the question exactly as before.
 * There is no "Also kick to FOH?" and no "Complete the ticket?" — the Stop
 * itself is the answer (server-side, same transaction).
 *
 * oct20 (FD-02 / FD-03 / D-2 / D-3):
 *   - the last technician may mark any job "Not finished" (an inspection
 *     answered "No" is one): the Stop then ends the work WITHOUT completing
 *     the ticket and hands it to the front office — the consequence line says
 *     so before they confirm;
 *   - a rollover day with a live next day says it ends today's work only;
 *   - a disabled Stop button always says why, next to it.
 *
 * OCT28 #14: the last technician on a day of a multi-day repair whose next day
 * is still booked chooses "Today's Work Complete" or "Entire Repair Complete"
 * (nothing preselected; Stop stays disabled until one is chosen). OCT28 #14-c:
 * an inspection is answered Completed / Incomplete / Not performed — "Not
 * performed" is not offered for an inspection with recorded work.
 *
 * Presentational only: the facts come from my_finish_work_context, the
 * commit goes through the Stage A executor into finish_my_work.
 */
import { useState, type ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ParkingSpotChoice } from '@/lib/location/api';
import {
  FINISH_DESTINATION_KINDS,
  STOP_SCOPE_CHOICES,
  dispositionOptions,
  stopConsequenceLine,
  type DestinationDraft,
  type JobDisposition,
  type StopConsequenceOptions,
  type StopDialogMode,
  type StopScope,
} from '@/lib/timer/interaction/simplifiedStop';

export interface StopWorkDialogProps {
  open: boolean;
  busy: boolean;
  endingPausedWork: boolean;
  /** null while the facts are read (or could not be read: the server still decides). */
  mode: StopDialogMode | null;
  /** Where the vehicle is now, in words (for FINAL_STAYS). */
  whereNow: string | null;
  /** The server asked for a destination (or refused one): the question is shown regardless of mode. */
  forceLocationQuestion: boolean;
  destination: DestinationDraft;
  onDestinationChange: (d: DestinationDraft) => void;
  /** #19 — the spot a camera found the vehicle in (pre-answered), if any. */
  cameraFoundSpotId?: string | null;
  /** #19 — the spot a camera suggests (highlighted, never selected), if any. */
  cameraSuggestedSpotId?: string | null;
  spots: readonly ParkingSpotChoice[];
  /** Spots the server just refused in this act — shown, never re-proposed. */
  refusedSpotIds: ReadonlySet<string>;
  /** Final Stop: jobs no measured work points at (or open inspections) — each needs an answer. */
  unresolvedJobs: ReadonlyArray<{ jobId: string; name: string; kind?: 'inspection' | null; worked?: boolean }>;
  jobDispositions: Readonly<Record<string, JobDisposition>>;
  onJobDispositionChange: (jobId: string, d: JobDisposition) => void;
  diagnosticSection: ReactNode | null;
  canConfirm: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  /** oct20: rollover day / withheld completion / Detail Specialist wording for the consequence line. */
  consequenceOptions?: StopConsequenceOptions;
  /** oct20 FD-02: open jobs (final Stop) the technician may leave "not finished". */
  openJobs?: ReadonlyArray<{ jobId: string; name: string }>;
  unfinishedJobIds?: ReadonlySet<string>;
  onToggleUnfinished?: (jobId: string) => void;
  /** oct20 D-3: why the Stop button is disabled — shown, never silent. */
  confirmBlockers?: readonly string[];
  /** oct20a S1: "What did your session find?" for a diagnosis this Stop does not end. */
  sessionConclusionSection?: ReactNode | null;
  /** OCT28 #14: the two-choice question (only when offersEntireRepairChoice). */
  scopeChoice?: { value: StopScope | null; onChange: (s: StopScope) => void } | null;
}

const NO_IDS: ReadonlySet<string> = new Set();

export function StopWorkDialog({
  open, busy, endingPausedWork, mode, whereNow, forceLocationQuestion, destination, onDestinationChange,
  cameraFoundSpotId = null, cameraSuggestedSpotId = null, spots, refusedSpotIds, unresolvedJobs, jobDispositions, onJobDispositionChange,
  diagnosticSection, canConfirm, onConfirm, onCancel,
  consequenceOptions, openJobs = [], unfinishedJobIds = NO_IDS, onToggleUnfinished, confirmBlockers = [],
  sessionConclusionSection = null, scopeChoice = null,
}: StopWorkDialogProps) {
  const [somewhereElse, setSomewhereElse] = useState(false);
  const askLocation = forceLocationQuestion || mode === 'FINAL_ASK_LOCATION' || (mode === 'FINAL_STAYS' && somewhereElse);
  const [changingCameraAnswer, setChangingCameraAnswer] = useState(false);
  const close = () => { setSomewhereElse(false); setChangingCameraAnswer(false); onCancel(); };
  const foundSpot = cameraFoundSpotId && !refusedSpotIds.has(cameraFoundSpotId)
    ? spots.find((s) => s.id === cameraFoundSpotId && !s.occupied) ?? null : null;
  // Shown only while the pre-answer is the current answer; any change → the question.
  const showFound = askLocation && !changingCameraAnswer && !!foundSpot
    && destination?.kind === 'PARKING_SPOT' && destination.parkingSpotId === foundSpot.id;
  const suggestedSpot = cameraSuggestedSpotId && !refusedSpotIds.has(cameraSuggestedSpotId)
    ? spots.find((s) => s.id === cameraSuggestedSpotId && !s.occupied) ?? null : null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="stop-work-dialog">
        <DialogHeader>
          {/* The last technician's Stop completes the ticket (the consequence
              line says so); with a colleague still working, only this
              technician's work ends. Either way the measured time goes to
              allocation review. */}
          <DialogTitle>{endingPausedWork ? 'End Paused Work?' : 'Stop Work?'}</DialogTitle>
          <DialogDescription data-testid="stop-consequence">
            {mode ? stopConsequenceLine(mode, whereNow, consequenceOptions) : 'Your measured time goes to allocation review.'}
          </DialogDescription>
        </DialogHeader>

        {scopeChoice && (
          <div className="grid gap-2" role="radiogroup" aria-label="What did you finish?" data-testid="stop-scope-choice">
            {STOP_SCOPE_CHOICES.map((c) => (
              <button key={c.value} type="button" role="radio" disabled={busy}
                      aria-checked={scopeChoice.value === c.value}
                      data-testid={`stop-scope-${c.value}`}
                      onClick={() => scopeChoice.onChange(c.value)}
                      className={cn('min-h-11 rounded-md border px-3 py-2 text-left',
                        scopeChoice.value === c.value ? 'border-primary bg-primary/10' : 'hover:bg-muted')}>
                <span className="block text-sm font-medium">{c.label}</span>
                <span className="block text-xs text-muted-foreground">{c.detail}</span>
              </button>
            ))}
          </div>
        )}

        {diagnosticSection}

        {sessionConclusionSection}

        {unresolvedJobs.length > 0 && (
          <div className="space-y-2 border-t pt-3" data-testid="stop-unresolved-jobs">
            <p className="text-sm font-medium">
              {unresolvedJobs.some((j) => j.kind === 'inspection') ? 'Say what happened to each of these:' : 'No work was recorded on these jobs. What happened?'}
            </p>
            {unresolvedJobs.map((j) => (
              <div key={j.jobId} className="space-y-1" data-testid={`stop-job-${j.jobId}`}>
                <p className="text-sm">{j.name}</p>
                <div className="flex flex-wrap gap-1" role="radiogroup" aria-label={`What happened to ${j.name}?`}>
                  {dispositionOptions(j).map(({ value: d, label }) => (
                    <button key={d} type="button" role="radio" disabled={busy}
                            aria-checked={jobDispositions[j.jobId] === d}
                            data-testid={`stop-job-${j.jobId}-${d}`}
                            onClick={() => onJobDispositionChange(j.jobId, d as JobDisposition)}
                            className={cn('rounded-md border px-3 py-1.5 text-sm',
                              jobDispositions[j.jobId] === d ? 'border-primary bg-primary/10 font-medium' : 'hover:bg-muted')}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <button type="button" className="text-left text-sm text-primary underline-offset-2 hover:underline"
                    data-testid="stop-return-to-work" disabled={busy} onClick={close}>
              Return to work — keep the timer running
            </button>
          </div>
        )}

        {openJobs.length > 0 && onToggleUnfinished && (
          <div className="space-y-2 border-t pt-3" data-testid="stop-open-jobs">
            <p className="text-sm font-medium">Is any work not finished?</p>
            <p className="text-xs text-muted-foreground">
              Mark a job only if it is not done. The ticket then is not completed — it goes to the front office with the work left open.
            </p>
            <div className="grid gap-1">
              {openJobs.map((j) => {
                const on = unfinishedJobIds.has(j.jobId);
                return (
                  <button key={j.jobId} type="button" role="checkbox" aria-checked={on} disabled={busy}
                          data-testid={`stop-unfinished-${j.jobId}`} onClick={() => onToggleUnfinished(j.jobId)}
                          className={cn('flex min-h-11 md:min-h-9 items-center justify-between rounded-md border px-3 py-1.5 text-left text-sm',
                            on ? 'border-primary bg-primary/10 font-medium' : 'hover:bg-muted')}>
                    <span>{j.name}</span>
                    <span className="text-xs text-muted-foreground">{on ? 'Not finished' : 'Done'}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {mode === 'FINAL_STAYS' && !askLocation && (
          <button type="button" className="text-left text-sm text-primary underline-offset-2 hover:underline"
                  data-testid="stop-vehicle-elsewhere" onClick={() => setSomewhereElse(true)}>
            The vehicle is somewhere else
          </button>
        )}

        {showFound && foundSpot && (
          <div className="flex items-center justify-between gap-2 border-t pt-3" data-testid="stop-location-found">
            <p className="text-sm font-medium">Found it in Spot {foundSpot.label}.</p>
            <Button variant="outline" size="sm" className="min-h-9" disabled={busy}
                    data-testid="stop-location-found-change" onClick={() => setChangingCameraAnswer(true)}>
              Change
            </Button>
          </div>
        )}

        {askLocation && !showFound && (
          <div className="space-y-2 border-t pt-3" data-testid="stop-location-question">
            <p className="text-sm font-medium">Where are you leaving the vehicle?</p>
            {suggestedSpot && (
              <p className="text-sm text-muted-foreground" data-testid="stop-location-suggested">
                Looks like Spot {suggestedSpot.label}.
              </p>
            )}
            <div className="grid gap-1" role="radiogroup" aria-label="Where are you leaving the vehicle?">
              {FINISH_DESTINATION_KINDS.map((k) => (
                <button key={k.kind} type="button" role="radio" disabled={busy}
                        aria-checked={destination?.kind === k.kind}
                        data-testid={`stop-dest-${k.kind}`}
                        onClick={() => onDestinationChange({ kind: k.kind } as DestinationDraft)}
                        className={cn('min-h-11 rounded-md border px-3 py-2 text-left text-sm md:min-h-0',
                          destination?.kind === k.kind ? 'border-primary bg-primary/10 font-medium' : 'hover:bg-muted')}>
                  {k.label}
                </button>
              ))}
            </div>

            {destination?.kind === 'PARKING_SPOT' && (
              <div className="grid grid-cols-3 gap-1 sm:grid-cols-4" role="radiogroup" aria-label="Parking spot">
                {spots.map((s) => (
                  <button key={s.id} type="button" role="radio" disabled={s.occupied || busy}
                          aria-checked={destination.parkingSpotId === s.id}
                          data-testid={`stop-spot-${s.id}`}
                          onClick={() => onDestinationChange({ kind: 'PARKING_SPOT', parkingSpotId: s.id })}
                          className={cn('rounded-md border p-2 text-left text-xs',
                            destination.parkingSpotId === s.id ? 'border-primary bg-primary/10'
                              : suggestedSpot?.id === s.id ? 'border-dashed border-primary/60 hover:bg-muted' : 'hover:bg-muted',
                            s.occupied && 'cursor-not-allowed opacity-50')}>
                    <span className="block font-medium">Spot {s.label}</span>
                    <span className="block text-muted-foreground">
                      {s.occupied ? 'Occupied' : refusedSpotIds.has(s.id) ? 'Just refused' : suggestedSpot?.id === s.id ? 'Looks like it’s here' : 'Free'}
                    </span>
                  </button>
                ))}
                {spots.length === 0 && (
                  <p className="col-span-full text-sm text-muted-foreground">
                    No numbered spots are set up. Choose general parking or exact spot unknown.
                  </p>
                )}
              </div>
            )}

          </div>
        )}

        {!canConfirm && !busy && confirmBlockers.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100"
               role="status" data-testid="stop-confirm-blockers">
            <p className="font-medium">Before you can stop:</p>
            <ul className="ml-4 list-disc">
              {confirmBlockers.map((b) => <li key={b}>{b}</li>)}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={busy}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={!canConfirm || busy} data-testid="stop-confirm">
            {busy ? (endingPausedWork ? 'Ending...' : 'Stopping...')
              : scopeChoice?.value === 'ENTIRE_REPAIR' ? 'Complete Entire Repair'
              : (endingPausedWork ? 'End Work' : 'Stop Work')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}